import { Alert, Anchor, Button, Divider, Group, PasswordInput, PinInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconKey } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useLoginSecondFactor } from '@/api/account';
import { ApiError } from '@/api/client';
import { useLogin, useSession } from '@/api/session';
import { discoverSso, usePublicBranding, type SsoDiscovery } from '@/api/sso';
import { useSystemInfo } from '@/api/system';
import { AuthLayout } from './AuthLayout';
import { loginErrorKey } from './loginErrors';
import { OAuthButtons } from './OAuthButtons';

/** Anmeldung mit E-Mail und Passwort (SEC-01), optional zweiter Faktor (SEC-03) und OAuth (SEC-02). */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const session = useSession();
  const login = useLogin();
  const secondFactor = useLoginSecondFactor();
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [sso, setSso] = useState<SsoDiscovery | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const system = useSystemInfo();
  const { data: branding } = usePublicBranding(window.location.host, params.get('ws'));
  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (/^\S+@\S+$/.test(v) ? null : t('login.invalidEmail')),
      password: (v) => (v ? null : t('login.passwordRequired')),
    },
  });
  const from = (location.state as { from?: string } | null)?.from ?? '/tracker';

  if (session.data?.authenticated) {
    return <Navigate to={from} replace />;
  }

  const error = (needsCode ? secondFactor.error : login.error) instanceof ApiError ? ((needsCode ? secondFactor.error : login.error) as ApiError) : null;
  const oauthError = params.get('error');
  const errorDetail = params.get('detail');
  const ssoUrl = sso?.available ? sso.loginUrl : branding?.ssoLoginUrl;
  const ssoName = sso?.available ? sso.workspaceName : branding?.name;
  const ssoRequired = error?.code === 'sso-required';
  const passwordHidden = !showPassword && ((sso?.available && sso.enforced) || (!sso && branding?.ssoEnforced));
  const checkSso = (email: string) => {
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    discoverSso(email).then((d) => setSso(d?.available ? d : null), () => setSso(null));
  };
  const done = () => navigate(from, { replace: true });

  const submitCode = (value: string) => secondFactor.mutate(value.trim(), { onSuccess: done });

  if (needsCode) {
    return (
      <AuthLayout branding={branding}>
        <Stack>
          <Title order={3} ta="center">
            {t('login.twoFactorTitle')}
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            {useRecovery ? t('login.recoveryHint') : t('login.twoFactorHint')}
          </Text>
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={18} />}>
              {error.code === 'invalid-code' ? t('login.invalidCode') : error.message}
            </Alert>
          )}
          {useRecovery ? (
            <TextInput value={code} onChange={(e) => setCode(e.currentTarget.value)} placeholder="12345-67890" autoFocus aria-label={t('login.recoveryCode')} />
          ) : (
            <Group justify="center">
              <PinInput length={6} type="number" oneTimeCode autoFocus value={code} onChange={setCode} onComplete={submitCode} aria-label={t('login.code')} />
            </Group>
          )}
          <Button fullWidth loading={secondFactor.isPending} onClick={() => submitCode(code)} disabled={code.length < 6}>
            {t('login.verify')}
          </Button>
          <Group justify="space-between">
            <Anchor size="sm" component="button" type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}>
              {useRecovery ? t('login.useApp') : t('login.useRecovery')}
            </Anchor>
            <Anchor size="sm" component="button" type="button" onClick={() => { setNeedsCode(false); setCode(''); }}>
              {t('common.cancel')}
            </Anchor>
          </Group>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout branding={branding}>
      <form
        onSubmit={form.onSubmit((values) =>
          login.mutate(values, {
            onSuccess: (result) => (result.twoFactorRequired ? setNeedsCode(true) : done()),
          }),
        )}
      >
        <Stack>
          <Title order={3} ta="center">
            {t('login.title')}
          </Title>
          {system.data?.maintenanceMessage && <Alert color="yellow">{t('login.maintenance', { message: system.data.maintenanceMessage })}</Alert>}
          {params.get('deleted') && <Alert color="green">{t('login.accountDeleted')}</Alert>}
          {(error || oauthError) && (
            <Alert color="red" icon={<IconAlertCircle size={18} />}>
              {error
                ? error.code === 'invalid-credentials'
                  ? t('login.invalidCredentials')
                  : error.code === 'too-many-attempts'
                    ? t('login.tooManyAttempts')
                    : ssoRequired
                      ? t('login.ssoRequired')
                      : error.message
                : t(loginErrorKey(oauthError))}
              {!error && errorDetail && oauthError !== 'oauth' && (
                <Text size="xs" mt={4}>
                  {errorDetail}
                </Text>
              )}
            </Alert>
          )}
          {(ssoUrl || ssoRequired) && (
            <Button
              component="a"
              href={ssoRequired ? String(error?.details.loginUrl ?? ssoUrl) : ssoUrl!}
              variant="filled"
              color={branding?.brandColor ?? undefined}
              leftSection={<IconKey size={16} />}
              fullWidth
            >
              {ssoName ? t('login.ssoWith', { name: ssoName }) : t('login.sso')}
            </Button>
          )}
          {(ssoUrl || ssoRequired) && <Divider label={t('login.or')} />}
          <TextInput
            label={t('login.email')}
            type="email"
            autoComplete="email"
            placeholder={t('common.emailPlaceholder')}
            {...form.getInputProps('email')}
            onBlur={(e) => {
              form.getInputProps('email').onBlur?.(e);
              checkSso(e.currentTarget.value);
            }}
          />
          {passwordHidden ? (
            <Anchor size="sm" component="button" type="button" onClick={() => setShowPassword(true)}>
              {t('login.usePassword')}
            </Anchor>
          ) : (
            <>
              <PasswordInput label={t('login.password')} autoComplete="current-password" {...form.getInputProps('password')} />
              <Group justify="flex-end" mt={-8}>
                <Anchor component={Link} to="/forgot-password" size="xs">
                  {t('login.forgotPassword')}
                </Anchor>
              </Group>
              <Button type="submit" fullWidth loading={login.isPending} variant={ssoUrl ? 'default' : 'filled'}>
                {t('login.submit')}
              </Button>
            </>
          )}
          <OAuthButtons />
          {system.data?.registrationOpen !== false && (
            <Text size="sm" ta="center">
              {t('login.noAccount')}{' '}
              <Anchor component={Link} to="/register">
                {t('login.register')}
              </Anchor>
            </Text>
          )}
        </Stack>
      </form>
    </AuthLayout>
  );
}
